import {
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import { overrideOrganizationHeader, parseApiError } from '@cast/api-client';
import { notify } from '@cast/design-system';
import { apiClient } from '@cast/infrastructure-api';
import { UpdateSettingsResponse } from '@cast/types';
import { getOrganizationSettingsQueryOptions } from '@cast/infrastructure-abilities';

type UseOrganizationCopilotSettingsMutation = UseMutationResult<
  AxiosResponse<UpdateSettingsResponse>,
  unknown,
  boolean
>;

export const useOrganizationCopilotSettingsMutation = ({
  organizationId,
  additionallyInvalidateOrgs = [],
}: {
  organizationId: string;
  additionallyInvalidateOrgs?: string[];
}): UseOrganizationCopilotSettingsMutation => {
  const queryClient = useQueryClient();
  return useMutation({
    networkMode: 'offlineFirst',
    mutationFn: (enabled) => {
      return apiClient.organizationSettings.updateOrganizationSettings(
        {
          organizationId,
          updateSettingsRequestSettings: {
            copilotSettings: {
              enabled,
            },
          },
        },
        {
          headers: overrideOrganizationHeader(organizationId),
        }
      );
    },
    onSettled: async () => {
      await Promise.all(
        [organizationId, ...additionallyInvalidateOrgs].map((id) =>
          queryClient.refetchQueries({
            queryKey: getOrganizationSettingsQueryOptions({
              organizationId: id,
            }).queryKey,
          })
        )
      );
      await queryClient.refetchQueries({
        queryKey: getOrganizationSettingsQueryOptions({ organizationId })
          .queryKey,
      });
    },
    onSuccess: (_, enabled) => {
      notify.success(
        enabled
          ? 'Copilot enabled successfully'
          : 'Copilot disabled successfully'
      );
    },
    onError: (error) => notify.error(parseApiError(error)),
  });
};
